/**
 * In-memory dispatch-board store for the new Activity-centric workflow.
 *
 * Tracks:
 *   - crews + their assemblage (members / vehicles / activities)
 *   - standalone activities (created but not yet pinned to a crew —
 *     these surface in the right rail's "Activities" tab)
 *   - consumedUnassignedIds (workOrderTaskIds linked into an activity
 *     so they disappear from the Unassigned tab)
 *
 * Real backend swap in S2 replaces this file via `client.ts`; component
 * code untouched.
 */

import type {
  ActivityAttachment,
  ComplianceActivityMeta,
  CaptureActivityPayload,
  CaptureSummary,
  CaptureSummaryItem,
  CrewColorToken,
  DispatchActivityTask,
  DispatchCrew,
  DispatchCrewJobActivity,
  DispatchJobType,
  DispatchVehicle,
  RollOverPayload,
  UnassignedJob,
} from '../types';

interface CrewDayState {
  crews: DispatchCrew[];
  /** Activities not yet assigned to any crew (the right-rail tab feed). */
  standaloneActivities: DispatchCrewJobActivity[];
  /** Source tasks materialised either as the activity seed OR as linked tasks. */
  consumedUnassignedIds: Set<string>;
  /** Auto-incrementing counter for activity numbers within the day. */
  nextActivitySeq: number;
}

let _activitySeq = 1;
const nextActivityId = (): string =>
  `act-${Date.now()}-${(_activitySeq++).toString(36)}`;
const padActivityNumber = (n: number): string =>
  `ACT${n.toString().padStart(6, '0')}`;

/**
 * localStorage key for the entire mock store snapshot. Versioned (`:v1`)
 * so a future shape change can be detected (hydrate would skip + the
 * Reset button regenerates a clean v2 payload). Bump the suffix when
 * `CrewDayState` shape changes incompatibly.
 *
 * Why localStorage (not session/IndexedDB):
 *   - Survives full page refresh AND tab close — what Brent expects on
 *     demo day when he reloads the tab.
 *   - Sync API, ~5 KB per crew at worst → entire payload fits inside the
 *     5–10 MB localStorage quota even for an aggressive multi-day demo.
 *   - One blob = one read, one write. No schema, no upgrades, simplest
 *     thing that works for a mock-only data layer.
 */
const STORAGE_KEY = 'fieldtrix:dispatch-board:mock-state:v1';

class MockStore {
  private days = new Map<string, CrewDayState>();
  private nextCrewIndex = 1;
  /**
   * Manually-created backlog tasks (Sprint A). Tenant-wide, NOT date-scoped
   * — an unscheduled task shows in every day's Unassigned rail until it's
   * linked to an activity (then the day's consumed-ids hide it). Mirrors a
   * manual WorkOrderTask sitting in the backlog. Persisted like the rest.
   */
  private backlogTasks: UnassignedJob[] = [];

  constructor() {
    this.hydrate();
    this.installAutoPersist();
  }

  /**
   * Auto-persist wrapper — listed explicitly (NOT reflected dynamically)
   * so a new mutation method MUST be added to this list to gain
   * persistence. Better to fail loudly (next refresh shows empty) than
   * silently not persist some action.
   *
   * Reset is deliberately excluded — it manages its own storage clear
   * and re-persisting after a wipe would be wasted work.
   */
  private installAutoPersist(): void {
    const persistMethods = [
      'createCrew',
      'deleteCrew',
      'renameCrew',
      'setCrewColor',
      'addMember',
      'removeMember',
      'setLead',
      'addVehicle',
      'removeVehicle',
      'createActivity',
      'assignActivityToCrew',
      'deleteActivity',
      'unassignActivityFromCrew',
      'moveActivityBetweenCrews',
      'updateActivity',
      'linkTaskToActivity',
      'unlinkTaskFromActivity',
      'addJobWorker',
      'removeJobWorker',
      'addJobVehicle',
      'removeJobVehicle',
      'cloneCrews',
      'markStaffAway',
      'captureActivity',
      'rollOverActivity',
      'createBacklogTask',
    ] as const;
    for (const name of persistMethods) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const self = this as unknown as Record<string, (...args: any[]) => any>;
      const original = self[name].bind(this);
      self[name] = (...args: unknown[]) => {
        const result = original(...args);
        this.persist();
        return result;
      };
    }
  }

  /* ------------------------------------------------------------------ */
  /* Persistence — localStorage round-trip                               */
  /* ------------------------------------------------------------------ */

  /**
   * Pull the previous session's snapshot out of localStorage if any.
   * Tolerant of missing/corrupt data — anything that fails just leaves
   * the store empty (same as a fresh boot).
   *
   * Sets need explicit array → Set rehydration; everything else round-
   * trips cleanly through JSON.
   */
  private hydrate(): void {
    try {
      if (typeof window === 'undefined') return;
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        days: Array<
          [
            string,
            {
              crews: DispatchCrew[];
              standaloneActivities: DispatchCrewJobActivity[];
              consumedUnassignedIds: string[];
              nextActivitySeq: number;
            },
          ]
        >;
        nextCrewIndex?: number;
        backlogTasks?: UnassignedJob[];
      };
      if (!Array.isArray(parsed?.days)) return;
      for (const [date, state] of parsed.days) {
        this.days.set(date, {
          crews: state.crews ?? [],
          standaloneActivities: state.standaloneActivities ?? [],
          consumedUnassignedIds: new Set(state.consumedUnassignedIds ?? []),
          nextActivitySeq: state.nextActivitySeq ?? 1,
        });
      }
      this.nextCrewIndex = parsed.nextCrewIndex ?? 1;
      this.backlogTasks = Array.isArray(parsed.backlogTasks)
        ? parsed.backlogTasks
        : [];
    } catch (e) {
      // Storage disabled, quota exceeded, or corrupt payload — silently
      // boot empty. The user can still build the day from scratch.
      // eslint-disable-next-line no-console
      console.warn('[dispatch-board] hydrate failed:', e);
    }
  }

  /**
   * Snapshot the entire store to localStorage. Called at the END of
   * every mutation method (one write per mutation — fine at human-click
   * frequencies, and the payload tops out at a few KB for a realistic
   * demo board).
   *
   * Set → Array conversion is required because Sets don't serialize
   * through JSON.stringify (they come out as `{}`).
   */
  private persist(): void {
    try {
      if (typeof window === 'undefined') return;
      // Strip `attachments` from activities before serialising. Each
      // attachment carries a base64 data URI (potentially several MB
      // for a photo); a few of those would blow past localStorage's
      // 5–10 MB quota and break the entire snapshot save. Attachments
      // intentionally do NOT survive a page refresh in the demo — the
      // backend wiring is post-demo, and this is a hard trade-off the
      // user explicitly accepted (in-memory only) to keep the rest of
      // the persistence reliable.
      const stripAttachments = (
        a: DispatchCrewJobActivity,
      ): DispatchCrewJobActivity => ({
        ...a,
        attachments: undefined,
      });
      const payload = {
        days: Array.from(this.days.entries()).map(([date, state]) => [
          date,
          {
            crews: state.crews.map((c) => ({
              ...c,
              jobs: c.jobs.map(stripAttachments),
            })),
            standaloneActivities:
              state.standaloneActivities.map(stripAttachments),
            consumedUnassignedIds: Array.from(state.consumedUnassignedIds),
            nextActivitySeq: state.nextActivitySeq,
          },
        ]),
        nextCrewIndex: this.nextCrewIndex,
        backlogTasks: this.backlogTasks,
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      // Storage full, blocked, or unavailable — log once but don't
      // throw. The mutation itself already succeeded in memory; loss
      // only happens if the user refreshes.
      // eslint-disable-next-line no-console
      console.warn('[dispatch-board] persist failed:', e);
    }
  }

  /**
   * Wipe everything — every crew, every activity, every day. Used by
   * the toolbar's Reset button when the dispatcher wants a clean
   * starting state. localStorage is cleared too so a refresh doesn't
   * resurrect the data.
   */
  reset(): void {
    this.days.clear();
    this.nextCrewIndex = 1;
    this.backlogTasks = [];
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[dispatch-board] reset clear failed:', e);
    }
  }

  private getOrInit(date: string): CrewDayState {
    if (!this.days.has(date)) {
      this.days.set(date, {
        crews: [],
        standaloneActivities: [],
        consumedUnassignedIds: new Set(),
        nextActivitySeq: 1,
      });
    }
    return this.days.get(date)!;
  }

  /* ------------------------------------------------------------------ */
  /* Read snapshot                                                       */
  /* ------------------------------------------------------------------ */
  getDay(date: string): {
    crews: DispatchCrew[];
    standaloneActivities: DispatchCrewJobActivity[];
    consumedUnassignedIds: string[];
    backlogTasks: UnassignedJob[];
  } {
    const s = this.getOrInit(date);
    return {
      crews: structuredClone(s.crews),
      standaloneActivities: structuredClone(s.standaloneActivities),
      consumedUnassignedIds: Array.from(s.consumedUnassignedIds),
      // Manually-created backlog (Sprint A) — tenant-wide; the hook merges
      // it into the Unassigned rail and filters by this day's consumed ids.
      backlogTasks: structuredClone(this.backlogTasks),
    };
  }

  /* ------------------------------------------------------------------ */
  /* Backlog tasks — manually created on the board (Sprint A)            */
  /* ------------------------------------------------------------------ */

  /** All manually-created backlog tasks (tenant-wide). The hook merges
   *  these into each day's Unassigned rail. */
  getBacklogTasks(): UnassignedJob[] {
    return structuredClone(this.backlogTasks);
  }

  /**
   * Create a backlog task — the board-first task-creation path. Produces
   * an UnassignedJob that surfaces in the Unassigned rail, draggable onto
   * an activity exactly like a quote-origin task. Real-API (7g-REAL) maps
   * this to POST /WorkOrderTask.
   */
  createBacklogTask(payload: {
    jobId: string;
    jobNumber: string;
    jobName: string;
    clientName: string;
    siteName: string;
    title: string;
    scaffoldTaskType: number;
    chargeUpType: number;
    isBillable: boolean;
    scaffoldCode?: string;
  }): UnassignedJob {
    const gen = `${Date.now()}-${(this.nextCrewIndex + this.backlogTasks.length).toString(36)}`;
    const typeFor = (n: number): DispatchJobType =>
      n === 1 ? 'ERECT'
      : n === 2 ? 'MODIFY'
      : n === 3 ? 'DISMANTLE'
      : n === 4 ? 'INSPECT'
      : n === 5 ? 'HANDOVER'
      : 'OTHER';
    const task: UnassignedJob = {
      kind: 'unplanned-task',
      // `wt-` prefix marks a board-created WorkOrderTask (vs planner backlog
      // ids) — handy for back-tracking + the real-API swap.
      id: `wt-${gen}`,
      jobId: payload.jobId,
      jobNumber: payload.jobNumber,
      jobName: payload.jobName,
      title: payload.title.trim(),
      type: typeFor(payload.scaffoldTaskType),
      taskType: payload.scaffoldTaskType,
      clientName: payload.clientName,
      siteName: payload.siteName,
      scaffoldCode: payload.scaffoldCode,
      createdAdhoc: true,
      chargeUpType: payload.chargeUpType,
      isBillable: payload.isBillable,
    };
    this.backlogTasks.push(task);
    return task;
  }

  /* ------------------------------------------------------------------ */
  /* Crew CRUD                                                           */
  /* ------------------------------------------------------------------ */
  createCrew(
    date: string,
    payload: { name?: string; colorToken?: CrewColorToken },
  ): DispatchCrew {
    const day = this.getOrInit(date);
    let n = 1;
    const usedNumbers = new Set(
      day.crews
        .map((c) => /^Crew\s+(\d+)$/.exec(c.name)?.[1])
        .filter(Boolean)
        .map(Number),
    );
    while (usedNumbers.has(n)) n++;
    const name = payload.name?.trim() || `Crew ${n}`;
    const colors: CrewColorToken[] = [
      'sky',
      'emerald',
      'amber',
      'rose',
      'violet',
      'slate',
    ];
    const crew: DispatchCrew = {
      id: `c-${Date.now()}-${this.nextCrewIndex++}`,
      name,
      position: day.crews.length,
      colorToken: payload.colorToken ?? colors[day.crews.length % colors.length],
      leadUserId: null,
      members: [],
      vehicles: [],
      jobs: [],
    };
    day.crews.push(crew);
    return crew;
  }

  deleteCrew(date: string, crewId: string): void {
    const day = this.getOrInit(date);
    const crew = day.crews.find((c) => c.id === crewId);
    if (!crew) return;
    // Cascade activities back to standalone pool (don't lose them).
    for (const act of crew.jobs) {
      day.standaloneActivities.push(act);
    }
    day.crews = day.crews.filter((c) => c.id !== crewId);
    day.crews.forEach((c, i) => (c.position = i));
  }

  renameCrew(date: string, crewId: string, name: string): void {
    const day = this.getOrInit(date);
    const crew = day.crews.find((c) => c.id === crewId);
    if (crew) crew.name = name;
  }

  setCrewColor(
    date: string,
    crewId: string,
    colorToken: CrewColorToken,
  ): void {
    const day = this.getOrInit(date);
    const crew = day.crews.find((c) => c.id === crewId);
    if (crew) crew.colorToken = colorToken;
  }

  /* ------------------------------------------------------------------ */
  /* Members + Vehicles (unchanged)                                      */
  /* ------------------------------------------------------------------ */
  addMember(
    date: string,
    crewId: string,
    member: { userId: string; name: string; role?: string },
  ): void {
    const day = this.getOrInit(date);
    const crew = day.crews.find((c) => c.id === crewId);
    if (!crew || crew.members.some((m) => m.userId === member.userId)) return;
    crew.members.push({
      userId: member.userId,
      name: member.name,
      role: member.role,
      isLead: crew.members.length === 0,
    });
    if (crew.members.length === 1) crew.leadUserId = member.userId;
  }

  removeMember(date: string, crewId: string, userId: string): void {
    const day = this.getOrInit(date);
    const crew = day.crews.find((c) => c.id === crewId);
    if (!crew) return;
    const wasLead = crew.leadUserId === userId;
    crew.members = crew.members.filter((m) => m.userId !== userId);
    if (wasLead) {
      crew.leadUserId = crew.members[0]?.userId ?? null;
      if (crew.members[0]) crew.members[0].isLead = true;
    }
  }

  setLead(date: string, crewId: string, userId: string): void {
    const day = this.getOrInit(date);
    const crew = day.crews.find((c) => c.id === crewId);
    if (!crew) return;
    crew.members = crew.members.map((m) => ({
      ...m,
      isLead: m.userId === userId,
    }));
    crew.leadUserId = userId;
  }

  addVehicle(date: string, crewId: string, vehicle: DispatchVehicle): void {
    const day = this.getOrInit(date);
    const dest = day.crews.find((c) => c.id === crewId);
    if (!dest) return;
    if (dest.vehicles.some((v) => v.assetId === vehicle.assetId)) return;
    dest.vehicles.push(vehicle);
  }

  removeVehicle(date: string, crewId: string, assetId: string): void {
    const day = this.getOrInit(date);
    const crew = day.crews.find((c) => c.id === crewId);
    if (!crew) return;
    crew.vehicles = crew.vehicles.filter((v) => v.assetId !== assetId);
  }

  /* ------------------------------------------------------------------ */
  /* Activity lifecycle — the new core flow                              */
  /* ------------------------------------------------------------------ */

  /**
   * Create a new activity. Optionally pre-assigns to a crew (when invoked
   * from a crew's "+" button) or leaves it standalone (when invoked from
   * the toolbar's "New Activity" button).
   *
   * Optionally seeds the activity with ANY NUMBER of initial tasks — each
   * task is consumed from the Unassigned pool. The activity is the
   * container, tasks are its execution line-items.
   *
   * Title is taken VERBATIM from the payload (auto-named "Activity {n}" by
   * the caller, user-editable). We deliberately do NOT derive it from a
   * task description anymore — keeps the title stable when tasks are added
   * or removed later.
   */
  createActivity(
    date: string,
    payload: {
      clientId: string;
      clientName: string;
      siteId: string;
      siteName: string;
      /** Parent Job — required at creation via the popup's mandatory Job picker. */
      jobId?: string;
      jobNumber?: string;
      /** Parent Job's charge-up type (1=Quoted, 2=VQ, 3=Charge Up, 4=Internal). */
      jobChargeUpType?: number;
      /** Hex colour of the picked site's SiteType, for the card's left edge. */
      siteTypeColorHex?: string;
      title: string;
      type: DispatchJobType;
      crewId?: string;
      notes?: string;
      initialTasks?: UnassignedJob[];
      /** Files + photos attached at creation. In-memory only (not persisted). */
      attachments?: ActivityAttachment[];
      /** Unplanned (phone-call) work — see DispatchCrewJobActivity.isUnplanned. */
      isUnplanned?: boolean;
      /** Description of the unplanned work (only when isUnplanned). */
      unplannedDescription?: string;
      /** Compliance-check flag + payload (mock prototype). */
      isCompliance?: boolean;
      compliance?: ComplianceActivityMeta;
    },
  ): DispatchCrewJobActivity {
    const day = this.getOrInit(date);
    const seq = day.nextActivitySeq++;
    // De-dupe + drop anything already consumed (safety vs. double-fire).
    const tasks = (payload.initialTasks ?? []).filter(
      (t, i, arr) =>
        !day.consumedUnassignedIds.has(t.id) &&
        arr.findIndex((x) => x.id === t.id) === i,
    );
    // JOB-INHERIT — same rule as `linkTaskToActivity`. When the popup
    // submitted tasks but no explicit Job pick (Job field left blank),
    // the activity inherits the first task's Job. Keeps the UI honest:
    // "this activity has tasks → it must belong to a Job" — UNPLANNED
    // badge is shown only when NEITHER Job NOR tasks are attached.
    const inheritedJobId = payload.jobId ?? tasks[0]?.jobId;
    const inheritedJobNumber = payload.jobNumber ?? tasks[0]?.jobNumber;
    const activity: DispatchCrewJobActivity = {
      activityId: nextActivityId(),
      sourceTaskId: tasks[0]?.id,
      jobNumber: padActivityNumber(seq),
      title: payload.title || `Activity ${seq}`,
      type: payload.type,
      status: 'Scheduled',
      scheduledStart: `${date}T08:00`,
      scheduledEnd: `${date}T17:00`,
      position: 0,
      clientId: payload.clientId,
      clientName: payload.clientName,
      siteId: payload.siteId,
      siteName: payload.siteName,
      parentJobId: inheritedJobId,
      parentJobNumber: inheritedJobNumber,
      parentJobChargeUpType: payload.jobChargeUpType,
      siteTypeColorHex: payload.siteTypeColorHex,
      notes: payload.notes?.trim() || undefined,
      // Unplanned flag + description carry straight through. We do NOT
      // generate a synthetic Job number / WorkOrder / ScaffoldTask in
      // the mock — backend wiring is post-demo (see adhoc-activity-plan.md).
      isUnplanned: payload.isUnplanned || undefined,
      unplannedDescription:
        payload.unplannedDescription?.trim() || undefined,
      isCompliance: payload.isCompliance || undefined,
      compliance: payload.compliance,
      tasks: tasks.map(mapUnassignedToTask),
      workers: [],
      attachments:
        payload.attachments && payload.attachments.length > 0
          ? payload.attachments
          : undefined,
    };
    for (const t of tasks) day.consumedUnassignedIds.add(t.id);
    if (payload.crewId) {
      const crew = day.crews.find((c) => c.id === payload.crewId);
      if (crew) {
        // Auto-assign every current crew member as a worker on the new
        // activity (per dispatcher feedback — the default expectation is
        // "this crew does this activity"). Workers can still be removed
        // individually via the avatar × on the card.
        activity.workers = crew.members.map((m) => ({
          userId: m.userId,
          name: m.name,
        }));
        // Mirror the same default for vehicles: the crew's gear flows
        // into the new activity as its starting vehicle list. Dispatcher
        // can remove individual ones via × on the chip, or drag EXTRA
        // assets onto the activity later for one-off jobs that need
        // additional vehicles (the "extra staff on this activity → need
        // an extra vehicle to reach the site" use case).
        replaceVehiclesWithCrewVehicles(activity, crew);
        activity.position = crew.jobs.length;
        crew.jobs.push(activity);
        return activity;
      }
    }
    day.standaloneActivities.push(activity);
    return activity;
  }

  /** Assign a standalone activity to a crew (drag from Activities tab). */
  assignActivityToCrew(
    date: string,
    activityId: string,
    crewId: string,
  ): void {
    const day = this.getOrInit(date);
    const idx = day.standaloneActivities.findIndex(
      (a) => a.activityId === activityId,
    );
    if (idx < 0) return;
    const dest = day.crews.find((c) => c.id === crewId);
    if (!dest) return;
    const activity = day.standaloneActivities[idx];
    day.standaloneActivities.splice(idx, 1);
    activity.position = dest.jobs.length;
    // Auto-assign destination crew's members (REPLACE — see comment on
    // `replaceWorkersWithCrewMembers`). Standalone activities have no
    // prior crew so the activity's existing workers list is dropped and
    // replaced with the new crew's members.
    replaceWorkersWithCrewMembers(activity, dest);
    // Mirror the same default for vehicles — activity inherits the
    // destination crew's vehicle pool. Standalone activities had no
    // host crew, so any prior `vehicles` list (e.g. extra vehicles
    // added while the activity was standalone) gets dropped here.
    replaceVehiclesWithCrewVehicles(activity, dest);
    dest.jobs.push(activity);
  }

  /**
   * Hard-delete an activity. Wherever it currently lives (crew jobs OR
   * standalone), remove it. Every linked task is released back to the
   * Unassigned pool so the dispatcher doesn't lose work.
   */
  deleteActivity(date: string, activityId: string): void {
    const day = this.getOrInit(date);
    // Helper that releases every task an activity carried.
    const releaseTasks = (act: DispatchCrewJobActivity) => {
      for (const t of act.tasks) {
        day.consumedUnassignedIds.delete(t.workOrderTaskId);
      }
    };
    // Crew jobs first
    for (const c of day.crews) {
      const idx = c.jobs.findIndex((j) => j.activityId === activityId);
      if (idx >= 0) {
        releaseTasks(c.jobs[idx]);
        c.jobs.splice(idx, 1);
        return;
      }
    }
    // Standalone
    const sIdx = day.standaloneActivities.findIndex(
      (a) => a.activityId === activityId,
    );
    if (sIdx >= 0) {
      releaseTasks(day.standaloneActivities[sIdx]);
      day.standaloneActivities.splice(sIdx, 1);
    }
  }

  /** Unassign an activity from its crew, returning it to the standalone list. */
  unassignActivityFromCrew(date: string, activityId: string): void {
    const day = this.getOrInit(date);
    for (const c of day.crews) {
      const idx = c.jobs.findIndex((j) => j.activityId === activityId);
      if (idx >= 0) {
        const act = c.jobs[idx];
        c.jobs.splice(idx, 1);
        day.standaloneActivities.push(act);
        return;
      }
    }
  }

  /** Move an activity between two crews. */
  moveActivityBetweenCrews(
    date: string,
    activityId: string,
    toCrewId: string,
  ): void {
    const day = this.getOrInit(date);
    for (const c of day.crews) {
      const idx = c.jobs.findIndex((j) => j.activityId === activityId);
      if (idx >= 0) {
        const act = c.jobs[idx];
        c.jobs.splice(idx, 1);
        const dest = day.crews.find((dc) => dc.id === toCrewId);
        if (!dest) {
          // Destination doesn't exist — fall back to standalone
          day.standaloneActivities.push(act);
          return;
        }
        act.position = dest.jobs.length;
        // REPLACE workers with the destination crew's members. The old
        // crew's members are dropped — dispatchers flagged the previous
        // merge behaviour as confusing because moving Activity from
        // Crew A → Crew B left A's workers on the activity, immediately
        // triggering "N not in Crew B" warnings.
        replaceWorkersWithCrewMembers(act, dest);
        // Same REPLACE rule for vehicles — see helper comment. Any
        // EXTRA vehicles the dispatcher had added to this activity on
        // its old crew get dropped here (the dispatcher will re-add
        // them if still needed in the destination crew context).
        replaceVehiclesWithCrewVehicles(act, dest);
        dest.jobs.push(act);
        return;
      }
    }
  }

  /**
   * Patch an existing activity in place. Used by the Edit popup.
   *
   *   - title / notes / jobId / jobNumber  → simple field updates
   *   - initialTasks                       → REPLACES the current task
   *                                          list; reconciles consumed ids
   *                                          (released for tasks dropped,
   *                                          consumed for tasks added)
   *
   * Searches BOTH crew jobs and standalone so it works regardless of the
   * activity's current home.
   */
  updateActivity(
    date: string,
    activityId: string,
    patch: {
      title?: string;
      notes?: string;
      jobId?: string | null;
      jobNumber?: string | null;
      /** New parent-Job charge type (or `null` to clear when Job is cleared). */
      jobChargeUpType?: number | null;
      /**
       * New date (YYYY-MM-DD) for the activity. ONLY valid for STANDALONE
       * activities — UI guards against using this on crew-bound activities
       * (changing date there would orphan from the crew's day). When the
       * date differs from `date`, the activity is moved to the new date's
       * bucket and its linked-task consumed-ids follow.
       */
      newDate?: string;
      initialTasks?: UnassignedJob[];
      /**
       * Full desired attachment list. REPLACES the existing one (same
       * pattern as initialTasks). Pass `undefined` to leave attachments
       * untouched; pass `[]` to clear all.
       */
      attachments?: ActivityAttachment[];
      /**
       * Unplanned-work description. Patchable so the Lead/Supervisor can
       * refine the description after the dispatcher captured it on the
       * phone. The `isUnplanned` flag itself is NOT patchable — mode is
       * fixed at creation to keep the audit trail clean.
       */
      unplannedDescription?: string;
    },
  ): void {
    let day = this.getOrInit(date);
    let activity = this.findActivity(day, activityId);
    if (!activity) return;

    // -------- Date change (standalone only) --------
    // If newDate is provided and differs, move the activity (and its
    // consumed task ids) across day buckets BEFORE applying other patches.
    if (
      patch.newDate &&
      patch.newDate !== date &&
      day.standaloneActivities.some((a) => a.activityId === activityId)
    ) {
      const idx = day.standaloneActivities.findIndex(
        (a) => a.activityId === activityId,
      );
      day.standaloneActivities.splice(idx, 1);
      // Release task ids from the source day's consumed pool
      for (const t of activity.tasks) {
        day.consumedUnassignedIds.delete(t.workOrderTaskId);
      }
      // Rewrite the scheduled timestamps so the date prefix matches
      activity.scheduledStart = swapDatePrefix(activity.scheduledStart, patch.newDate);
      activity.scheduledEnd = swapDatePrefix(activity.scheduledEnd, patch.newDate);
      // Land on the new day's bucket
      const toDay = this.getOrInit(patch.newDate);
      toDay.standaloneActivities.push(activity);
      for (const t of activity.tasks) {
        toDay.consumedUnassignedIds.add(t.workOrderTaskId);
      }
      // Subsequent field patches operate on the new day's snapshot
      day = toDay;
    }

    if (patch.title !== undefined) activity.title = patch.title;
    if (patch.notes !== undefined) {
      activity.notes = patch.notes.trim() || undefined;
    }
    if (patch.jobId !== undefined) {
      activity.parentJobId = patch.jobId || undefined;
    }
    if (patch.jobNumber !== undefined) {
      activity.parentJobNumber = patch.jobNumber || undefined;
    }
    if (patch.jobChargeUpType !== undefined) {
      activity.parentJobChargeUpType = patch.jobChargeUpType ?? undefined;
    }
    if (patch.unplannedDescription !== undefined) {
      activity.unplannedDescription =
        patch.unplannedDescription.trim() || undefined;
    }

    if (patch.attachments !== undefined) {
      // REPLACE semantics — the modal sends the full desired list every
      // time (including additions, keeping existing, and removals).
      // `undefined` means "leave untouched"; `[]` means "clear all".
      activity.attachments =
        patch.attachments.length > 0 ? patch.attachments : undefined;
    }

    if (patch.initialTasks) {
      const desiredIds = new Set(patch.initialTasks.map((t) => t.id));
      const currentIds = new Set(
        activity.tasks.map((t) => t.workOrderTaskId),
      );

      // Drop tasks not in the new set → release them back to Unassigned.
      for (const t of activity.tasks) {
        if (!desiredIds.has(t.workOrderTaskId)) {
          day.consumedUnassignedIds.delete(t.workOrderTaskId);
        }
      }
      // Keep only the still-desired ones.
      activity.tasks = activity.tasks.filter((t) =>
        desiredIds.has(t.workOrderTaskId),
      );
      // Append newly-desired tasks not already linked.
      for (const u of patch.initialTasks) {
        if (!currentIds.has(u.id)) {
          activity.tasks.push(mapUnassignedToTask(u));
          day.consumedUnassignedIds.add(u.id);
        }
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Task ↔ Activity linkage                                             */
  /* ------------------------------------------------------------------ */

  /** Link an unplanned job task as an execution line on an activity. */
  linkTaskToActivity(
    date: string,
    activityId: string,
    task: UnassignedJob,
  ): void {
    const day = this.getOrInit(date);
    const activity = this.findActivity(day, activityId);
    if (!activity) return;
    if (day.consumedUnassignedIds.has(task.id)) return;
    // JOB-MATCH GUARD — defence in depth. The page-level handler shows the
    // user-facing error; this block is the last line of defence so the
    // store can never end up with mismatched-Job tasks even from
    // programmatic / optimistic / future code paths.
    if (
      activity.parentJobNumber &&
      task.jobNumber !== activity.parentJobNumber
    ) {
      return;
    }
    // JOB-INHERIT — when the activity has no parent Job (i.e. it was
    // created as unplanned: client phoned in, dispatcher didn't pick a
    // Job), the FIRST task dropped onto it pins the activity to that
    // task's Job. The UI's UNPLANNED badge is derived from `!parentJobNumber`,
    // so this transition is what makes the violet badge disappear once
    // a real task is attached. Subsequent task drops are then guarded by
    // the JOB-MATCH check above (same-Job-only).
    if (!activity.parentJobNumber && task.jobNumber) {
      activity.parentJobId = task.jobId || activity.parentJobId;
      activity.parentJobNumber = task.jobNumber;
      // parentJobChargeUpType is the JOB-level charge chip; we leave it for
      // the backend/Job to resolve. The task's own charge override (set when
      // a board-created task chose QT/VQ/CU) rides on the activity TASK via
      // mapUnassignedToTask, not on the activity's Job-level chip.
    }
    activity.tasks.push(mapUnassignedToTask(task));
    day.consumedUnassignedIds.add(task.id);
  }

  /** Unlink a task from an activity (returns the source task to backlog). */
  unlinkTaskFromActivity(
    date: string,
    activityId: string,
    taskId: string,
  ): void {
    const day = this.getOrInit(date);
    const activity = this.findActivity(day, activityId);
    if (!activity) return;
    const idx = activity.tasks.findIndex((t) => t.taskId === taskId);
    if (idx < 0) return;
    const task = activity.tasks[idx];
    activity.tasks.splice(idx, 1);
    day.consumedUnassignedIds.delete(task.workOrderTaskId);
  }

  private findActivity(
    day: CrewDayState,
    activityId: string,
  ): DispatchCrewJobActivity | undefined {
    for (const c of day.crews) {
      const a = c.jobs.find((j) => j.activityId === activityId);
      if (a) return a;
    }
    return day.standaloneActivities.find((a) => a.activityId === activityId);
  }

  /* ------------------------------------------------------------------ */
  /* Job workers — explicit per-activity assignments                     */
  /* ------------------------------------------------------------------ */

  addJobWorker(
    date: string,
    activityId: string,
    worker: { userId: string; name: string },
  ): void {
    const day = this.getOrInit(date);
    const activity = this.findActivity(day, activityId);
    if (!activity) return;
    if (activity.workers.some((w) => w.userId === worker.userId)) return;
    activity.workers.push({ userId: worker.userId, name: worker.name });
  }

  removeJobWorker(date: string, activityId: string, userId: string): void {
    const day = this.getOrInit(date);
    const activity = this.findActivity(day, activityId);
    if (!activity) return;
    activity.workers = activity.workers.filter((w) => w.userId !== userId);
  }

  /**
   * Add an asset to an activity's vehicle list.
   *
   * Used when the dispatcher drags an asset from the Assets rail directly
   * onto an activity card (not onto the whole crew). The asset may or may
   * not already be in the parent crew's vehicle pool — if it isn't, the
   * card will flag it as "external" to make the call-out visible.
   *
   * Dedupes by assetId. Strips `allocations` from the stored entry —
   * that field is a derived/read-time concept; on the activity we keep
   * only the minimal asset shape.
   */
  addJobVehicle(
    date: string,
    activityId: string,
    vehicle: {
      assetId: string;
      code: string;
      label: string;
      assetClass?: string;
      imageUrl?: string;
    },
  ): void {
    const day = this.getOrInit(date);
    const activity = this.findActivity(day, activityId);
    if (!activity) return;
    if (!activity.vehicles) activity.vehicles = [];
    if (activity.vehicles.some((v) => v.assetId === vehicle.assetId)) return;
    activity.vehicles.push({
      assetId: vehicle.assetId,
      code: vehicle.code,
      label: vehicle.label,
      assetClass: vehicle.assetClass,
      imageUrl: vehicle.imageUrl,
    });
  }

  /** Remove a single asset from an activity's vehicle list. */
  removeJobVehicle(date: string, activityId: string, assetId: string): void {
    const day = this.getOrInit(date);
    const activity = this.findActivity(day, activityId);
    if (!activity) return;
    if (!activity.vehicles) return;
    activity.vehicles = activity.vehicles.filter((v) => v.assetId !== assetId);
    // Collapse empty array back to undefined so payloads stay minimal +
    // the card's "show this row" predicate stays clean.
    if (activity.vehicles.length === 0) activity.vehicles = undefined;
  }

  /* ------------------------------------------------------------------ */
  /* Copy Day — clone a subset of crews from another date                */
  /* ------------------------------------------------------------------ */

  /**
   * Clone the selected crews from `fromDate` onto `toDate`. Append-only —
   * existing crews on `toDate` are kept; the cloned crews land after them
   * in position order.
   *
   * What gets cloned (per the locked plan in docs/copy-day-plan.md):
   *   - name (verbatim — "Crew 1" stays "Crew 1")
   *   - colorToken
   *   - members (shallow copies; isLead flag preserved)
   *   - vehicles (shallow copies — allocations recompute at read time)
   *   - leadUserId
   *
   * What does NOT get cloned:
   *   - jobs / activities — today is fresh work. Yesterday's job list
   *     stays on yesterday; cloning the people + tools, not the work.
   *
   * Each cloned crew gets a brand-new id so the source and clone are
   * independent entities (editing one doesn't touch the other).
   *
   * Returns `{ clonedCount }` so the toast can be specific.
   */
  cloneCrews(
    fromDate: string,
    toDate: string,
    options: { crewIds: string[] },
  ): { clonedCount: number } {
    if (fromDate === toDate) {
      // Same-day clone is allowed in principle, but the UI only ever
      // surfaces this action on an EMPTY target day — so in practice
      // fromDate ≠ toDate always. Guard anyway: still works, just
      // duplicates entries with new ids.
    }
    const source = this.getOrInit(fromDate);
    const target = this.getOrInit(toDate);
    const idsToClone = new Set(options.crewIds);
    let clonedCount = 0;

    // Iterate in source position order so the clones preserve the same
    // visual sequence on the target day (Crew 1 → Crew 2 → Backup).
    const ordered = [...source.crews].sort((a, b) => a.position - b.position);
    for (const src of ordered) {
      if (!idsToClone.has(src.id)) continue;
      const cloned: DispatchCrew = {
        id: `c-${Date.now()}-${this.nextCrewIndex++}`,
        name: src.name,
        // Append after whatever's already on the target day. Empty-board
        // trigger means this is usually 0, but guard for the future case
        // where the UI re-enables Copy Day on non-empty boards.
        position: target.crews.length + clonedCount,
        colorToken: src.colorToken,
        leadUserId: src.leadUserId,
        members: src.members.map((m) => ({ ...m })),
        vehicles: src.vehicles.map((v) => ({ ...v })),
        // Activities deliberately omitted — today starts fresh.
        jobs: [],
      };
      target.crews.push(cloned);
      clonedCount++;
    }
    return { clonedCount };
  }

  /* ------------------------------------------------------------------ */
  /* Bulk staff removal — "mark away today"                              */
  /* ------------------------------------------------------------------ */

  /**
   * Strip a single user from EVERY engagement on the given day:
   *   - removed from every crew's `members` list
   *   - if they were a crew lead, `leadUserId` is cleared (no auto-promote;
   *     dispatcher picks the new lead deliberately)
   *   - removed from `workers` on every activity (crew-bound AND standalone)
   *
   * Used by the Staff Rail's ⊘ "mark away" action for the "Sarah just
   * called in sick" scenario — one click cleans a worker off the entire
   * day. Crews and activities themselves are NEVER deleted; only the
   * user's presence on them.
   *
   * Returns counts for the success toast:
   *   { crewsRemovedFrom, activitiesRemovedFrom }
   * - crewsRemovedFrom counts crews where they were a member OR a worker
   *   on any of that crew's activities (deduped per crew).
   * - activitiesRemovedFrom counts every activity they were on as a
   *   worker (crew + standalone), summed across the day.
   */
  markStaffAway(
    date: string,
    userId: string,
  ): { crewsRemovedFrom: number; activitiesRemovedFrom: number } {
    const day = this.getOrInit(date);
    const touchedCrewIds = new Set<string>();
    let activitiesRemovedFrom = 0;

    for (const crew of day.crews) {
      let crewTouched = false;
      // Members + lead
      if (crew.members.some((m) => m.userId === userId)) {
        crew.members = crew.members.filter((m) => m.userId !== userId);
        if (crew.leadUserId === userId) {
          // Intentional: no auto-promote. Brent picks the replacement
          // lead manually so the choice is conscious.
          crew.leadUserId = null;
        }
        crewTouched = true;
      }
      // Workers on every activity owned by this crew
      for (const act of crew.jobs) {
        if (act.workers.some((w) => w.userId === userId)) {
          act.workers = act.workers.filter((w) => w.userId !== userId);
          activitiesRemovedFrom++;
          crewTouched = true;
        }
      }
      if (crewTouched) touchedCrewIds.add(crew.id);
    }

    // Standalone activities — also need a clean (these don't belong to a
    // crew but the user could still be in their workers list, e.g. if
    // they were on an activity that later got unassigned from its crew).
    for (const act of day.standaloneActivities) {
      if (act.workers.some((w) => w.userId === userId)) {
        act.workers = act.workers.filter((w) => w.userId !== userId);
        activitiesRemovedFrom++;
      }
    }

    return {
      crewsRemovedFrom: touchedCrewIds.size,
      activitiesRemovedFrom,
    };
  }

  /* ------------------------------------------------------------------ */
  /* End-of-day Capture + Roll-over (Phase 7g — mock-first demo)         */
  /* ------------------------------------------------------------------ */

  /**
   * Apply a capture to an activity. Persists hour entries + per-task
   * execution status + per-asset km/usage. Marks the activity as
   * `isCaptured` and stamps `capturedAt`. Re-capture is allowed (no
   * lock) — mock always treats `isLockedByInvoice` as false; real
   * backend will enforce.
   *
   * The capture only writes the fields explicitly provided. Tasks not
   * in `taskCaptures` keep their previous executionStatus (default
   * 'Pending' on creation). Same rule for assetCaptures.
   */
  captureActivity(date: string, payload: CaptureActivityPayload): void {
    const day = this.getOrInit(date);
    const activity = this.findActivity(day, payload.activityId);
    if (!activity) return;

    // ── Attach backlog tasks staged in the capture form ("we now know
    //    which task we actually did"). Same rules as linkTaskToActivity:
    //    JOB-MATCH guard + JOB-INHERIT for unplanned activities. Hours
    //    travel with the link — the row is created already-captured.
    for (const at of payload.attachTasks ?? []) {
      const u = at.task;
      if (day.consumedUnassignedIds.has(u.id)) continue; // already linked somewhere
      if (
        activity.parentJobNumber &&
        u.jobNumber &&
        u.jobNumber !== activity.parentJobNumber
      ) {
        continue; // JOB-MATCH guard — silently skip mismatches (UI prevents)
      }
      if (!activity.parentJobNumber && u.jobNumber) {
        // JOB-INHERIT — first attached task pins the unplanned activity
        // to its Job; the UNPLANNED badge resolves itself.
        activity.parentJobId = u.jobId || activity.parentJobId;
        activity.parentJobNumber = u.jobNumber;
      }
      activity.tasks.push({
        taskId: `t-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
        workOrderTaskId: u.id,
        jobNumber: u.jobNumber,
        jobName: u.jobName,
        description: u.title,
        type: u.type,
        taskTypeRaw: u.taskType,
        scaffoldCode: u.scaffoldCode,
        clientName: u.clientName,
        siteName: u.siteName,
        // Carry a board-created task's charge override + billable flag
        // through attach-at-capture too (same rule as mapUnassignedToTask).
        chargeUpType: u.chargeUpType,
        isBillable: u.isBillable,
        executionStatus: at.status,
        perWorkerHours: at.perWorkerHours,
        roleHours: at.roleHours,
        captureNotes: at.notes?.trim() || undefined,
      });
      day.consumedUnassignedIds.add(u.id);
    }

    // ── Create ad-hoc tasks staged in the capture form — work done that
    //    matches no existing WorkOrderTask. Flagged isAdhoc + explicit
    //    chargeUpType (admin's call; overrides the Job-fallback chain).
    //    Real-API wiring creates these via the backend's AdHocTask path.
    for (const ah of payload.adhocTasks ?? []) {
      const desc = ah.description.trim();
      if (!desc) continue;
      const gen = `${Date.now()}-${Math.random().toString(36).slice(2, 5)}`;
      // ScaffoldTaskType → FE DispatchJobType (same mapping as mappers.ts).
      const typeFor = (n: number): DispatchJobType =>
        n === 1 ? 'ERECT'
        : n === 2 ? 'MODIFY'
        : n === 3 ? 'DISMANTLE'
        : n === 4 ? 'INSPECT'
        : n === 5 ? 'HANDOVER'
        : 'OTHER';
      activity.tasks.push({
        taskId: `t-${gen}`,
        workOrderTaskId: `adhoc-${gen}`, // no real WO task in mock
        jobNumber: activity.parentJobNumber ?? '',
        jobName: '',
        description: desc,
        type: typeFor(ah.taskType),
        taskTypeRaw: ah.taskType,
        // Cascade targeting: WO ref ('DAY-WORKS' or package ref) + the
        // scaffold within it ('SITE-WORKS' = Day Works' billing anchor).
        scaffoldCode: ah.scaffoldCode,
        adhocWorkOrderRef: ah.workOrderRef,
        // Dismantle-partial: % struck — drives PartialStrip (rental rate
        // reduction) at real-API capture.
        partialStrikePercent: ah.partialStrikePercent,
        clientName: activity.clientName,
        siteName: activity.siteName,
        executionStatus: ah.status,
        perWorkerHours: ah.perWorkerHours,
        captureNotes: ah.notes?.trim() || undefined,
        isAdhoc: true,
        chargeUpType: ah.chargeUpType,
        // Explicit false = captured-for-cost but excluded from invoicing.
        isBillable: ah.isBillable ? undefined : false,
      });
    }

    // Activity-level hours
    if (payload.travelHours !== undefined)
      activity.travelHours = payload.travelHours;
    if (payload.loadingHours !== undefined)
      activity.loadingHours = payload.loadingHours;
    if (payload.standbyHours !== undefined)
      activity.standbyHours = payload.standbyHours;

    // Per-task captures — match by taskId (the FE row id)
    const taskById = new Map(activity.tasks.map((t) => [t.taskId, t]));
    for (const tc of payload.taskCaptures) {
      const task = taskById.get(tc.taskId);
      if (!task) continue;
      task.executionStatus = tc.status;
      if (tc.perWorkerHours !== undefined)
        task.perWorkerHours = tc.perWorkerHours;
      if (tc.roleHours !== undefined) task.roleHours = tc.roleHours;
      if (tc.notes !== undefined)
        task.captureNotes = tc.notes.trim() || undefined;
    }

    // Per-vehicle captures — match by `rowId ?? assetId`. CRITICAL: the
    // mock-store does NOT generate a `rowId` for activity vehicles (only
    // the Phase 7b backend mapper does, from DbDispatchActivityAsset.Id).
    // So in mock mode every vehicle's rowId is undefined and we MUST fall
    // back to assetId — which is unique within an activity's vehicle list
    // (addVehicle/addJobVehicle both dedupe by assetId). The M3 capture
    // form builds `assetRowId` the same way (`v.rowId ?? v.assetId`), so
    // the key matches in both mock and real-API mode (real prefers rowId).
    if (activity.vehicles) {
      const vehById = new Map(
        activity.vehicles.map((v) => [v.rowId ?? v.assetId, v]),
      );
      for (const ac of payload.assetCaptures) {
        const veh = vehById.get(ac.assetRowId);
        if (!veh) continue;
        if (ac.km !== undefined) veh.km = ac.km;
        if (ac.usageHours !== undefined) veh.usageHours = ac.usageHours;
        veh.isRoundTrip = ac.isRoundTrip;
        if (ac.notes !== undefined)
          veh.captureNotes = ac.notes.trim() || undefined;
      }
    }

    activity.isCaptured = true;
    activity.capturedAt = new Date().toISOString();
    activity.isLockedByInvoice = false; // mock: never locked

    // ── COMPLIANCE CHAIN — for every Inspection captured Completed with
    //    a nextInspectionDate (admin-set, +7d default), spawn the next
    //    visit as a fresh standalone activity on that day. Real-API will
    //    create it as a WorkOrderTask so it lands in the backlog rail;
    //    the mock lands it on the target day's Activities rail directly
    //    so the chain is VISIBLE in the demo.
    const spawnNextVisit = (
      targetDate: string,
      scaffoldCode: string | undefined,
    ) => {
      if (!targetDate || targetDate === date) return; // can't chain to today
      const targetDay = this.getOrInit(targetDate);
      const seq = targetDay.nextActivitySeq++;
      const gen = `${Date.now()}-${Math.random().toString(36).slice(2, 5)}`;
      const label =
        scaffoldCode && scaffoldCode !== 'SITE-WORKS'
          ? scaffoldCode
          : activity.siteName || activity.title;
      targetDay.standaloneActivities.push({
        activityId: nextActivityId(),
        jobNumber: padActivityNumber(seq),
        parentJobId: activity.parentJobId,
        parentJobNumber: activity.parentJobNumber,
        parentJobChargeUpType: activity.parentJobChargeUpType,
        title: `Compliance visit — ${label}`,
        type: 'INSPECT',
        status: 'Scheduled',
        scheduledStart: `${targetDate}T08:00`,
        scheduledEnd: `${targetDate}T17:00`,
        position: 0,
        clientId: activity.clientId,
        clientName: activity.clientName,
        siteId: activity.siteId,
        siteName: activity.siteName,
        siteTypeColorHex: activity.siteTypeColorHex,
        tasks: [
          {
            taskId: `t-${gen}`,
            workOrderTaskId: `insp-${gen}`, // real backend: a WorkOrderTask
            jobNumber: activity.parentJobNumber ?? '',
            jobName: '',
            description: 'Routine inspection (7-day compliance)',
            type: 'INSPECT',
            taskTypeRaw: 4,
            scaffoldCode,
            clientName: activity.clientName,
            siteName: activity.siteName,
            executionStatus: 'Pending',
          },
        ],
        workers: [],
      });
    };

    for (const tc of payload.taskCaptures) {
      if (!tc.nextInspectionDate) continue;
      spawnNextVisit(tc.nextInspectionDate, taskById.get(tc.taskId)?.scaffoldCode);
    }
    for (const at of payload.attachTasks ?? []) {
      if (at.nextInspectionDate)
        spawnNextVisit(at.nextInspectionDate, at.task.scaffoldCode);
    }
    for (const ah of payload.adhocTasks ?? []) {
      if (ah.nextInspectionDate)
        spawnNextVisit(ah.nextInspectionDate, ah.scaffoldCode);
    }
  }

  /**
   * Roll over selected incomplete tasks onto target dates. Creates one
   * continuation activity per distinct (targetDate, targetCrewId) bucket
   * — mirrors how the dispatcher would expect the rolled-over work to
   * cluster on tomorrow's board.
   *
   * The source activity keeps its captured state intact; only the
   * leftover tasks move. Rolled tasks are REMOVED from the source so
   * the next time the source activity is viewed, only the captured
   * tasks remain.
   *
   * Continuation chain bookkeeping: each new activity gets
   * `continuationOfActivityId = sourceActivityId` and `continuationStep`
   * = source's step + 1. A freshly-created (root) activity has step 1,
   * so its first continuation is step 2.
   */
  rollOverActivity(date: string, payload: RollOverPayload): void {
    const sourceDay = this.getOrInit(date);
    const source = this.findActivity(sourceDay, payload.sourceActivityId);
    if (!source || payload.tasks.length === 0) return;

    // Group target tasks by (targetDate, targetCrewId) so we make as
    // few continuation activities as possible. Skip any task whose
    // targetDate equals the source date — "rolling to today" is a no-op
    // and would corrupt consumed-id bookkeeping (the target-add and
    // source-delete would hit the SAME day's set, net-cancelling).
    type BucketKey = string; // `${targetDate}__${targetCrewId ?? ''}`
    const buckets = new Map<
      BucketKey,
      { targetDate: string; targetCrewId: string | null; taskIds: Set<string> }
    >();
    for (const t of payload.tasks) {
      if (t.targetDate === date) continue; // no-op: can't roll to the same day
      const crewKey = t.targetCrewId ?? '';
      const key: BucketKey = `${t.targetDate}__${crewKey}`;
      let bucket = buckets.get(key);
      if (!bucket) {
        bucket = {
          targetDate: t.targetDate,
          targetCrewId: t.targetCrewId ?? null,
          taskIds: new Set(),
        };
        buckets.set(key, bucket);
      }
      bucket.taskIds.add(t.taskId);
    }

    const taskById = new Map(source.tasks.map((t) => [t.taskId, t]));
    const rolledOverIds = new Set<string>();
    const nextStep = (source.continuationStep ?? 1) + 1;

    for (const bucket of buckets.values()) {
      const targetDay = this.getOrInit(bucket.targetDate);
      const tasksForBucket = Array.from(bucket.taskIds)
        .map((id) => taskById.get(id))
        .filter((t): t is DispatchActivityTask => Boolean(t));
      if (tasksForBucket.length === 0) continue;

      const seqNumber = targetDay.nextActivitySeq++;
      const newActivity: DispatchCrewJobActivity = {
        activityId: nextActivityId(),
        jobNumber: padActivityNumber(seqNumber),
        parentJobId: source.parentJobId,
        parentJobNumber: source.parentJobNumber,
        parentJobChargeUpType: source.parentJobChargeUpType,
        title: source.title, // carry over for continuity
        type: source.type,
        status: 'Scheduled',
        scheduledStart: `${bucket.targetDate}T08:00`,
        scheduledEnd: `${bucket.targetDate}T17:00`,
        position: 0,
        clientId: source.clientId,
        clientName: source.clientName,
        siteId: source.siteId,
        siteName: source.siteName,
        siteTypeColorHex: source.siteTypeColorHex,
        notes: undefined,
        tasks: tasksForBucket.map((src) => ({
          // Fresh row ids for the continuation — same workOrderTaskId
          // so the unplanned-rail visibility rule still sees the task
          // as "scheduled today" once this activity lands on its date.
          taskId: `t-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
          workOrderTaskId: src.workOrderTaskId,
          jobNumber: src.jobNumber,
          jobName: src.jobName,
          description: src.description,
          type: src.type,
          taskTypeRaw: src.taskTypeRaw,
          scaffoldCode: src.scaffoldCode,
          clientName: src.clientName,
          siteName: src.siteName,
          // Carry the task's COMMERCIAL IDENTITY onto the continuation —
          // a rolled-over line bills exactly where the original did. This
          // includes the ad-hoc WO anchor so a continued ad-hoc line keeps
          // its Day-Works/package target (the anchor the whole ad-hoc model
          // depends on) instead of degrading to a no-WO orphan.
          chargeUpType: src.chargeUpType,
          isBillable: src.isBillable,
          isAdhoc: src.isAdhoc,
          adhocWorkOrderRef: src.adhocWorkOrderRef,
          // NOT carried: partialStrikePercent + perWorkerHours + captureNotes
          // are per-capture measurements — the continuation captures its own
          // tomorrow. executionStatus resets so the new day starts Pending.
          executionStatus: 'Pending',
        })),
        workers: [],
        continuationOfActivityId: source.activityId,
        continuationStep: nextStep,
      };

      if (bucket.targetCrewId) {
        const crew = targetDay.crews.find((c) => c.id === bucket.targetCrewId);
        if (crew) {
          // Auto-assign crew members + vehicles like a normal create
          newActivity.workers = crew.members.map((m) => ({
            userId: m.userId,
            name: m.name,
          }));
          newActivity.vehicles = crew.vehicles.map((v) => ({
            assetId: v.assetId,
            code: v.code,
            label: v.label,
            assetClass: v.assetClass,
            imageUrl: v.imageUrl,
          }));
          newActivity.position = crew.jobs.length;
          crew.jobs.push(newActivity);
        } else {
          // Crew not on target day — fall back to standalone
          targetDay.standaloneActivities.push(newActivity);
        }
      } else {
        targetDay.standaloneActivities.push(newActivity);
      }

      // Mark the source's WO task ids as consumed on the target day
      // (matches the day's unplanned-rail filter).
      for (const t of tasksForBucket) {
        targetDay.consumedUnassignedIds.add(t.workOrderTaskId);
        rolledOverIds.add(t.taskId);
      }
    }

    // Remove rolled-over tasks from the source activity + release their
    // WO ids from the source day's consumed pool (the task is now on
    // the target day's bucket).
    if (rolledOverIds.size > 0) {
      const remainingTasks = source.tasks.filter(
        (t) => !rolledOverIds.has(t.taskId),
      );
      for (const t of source.tasks) {
        if (rolledOverIds.has(t.taskId)) {
          sourceDay.consumedUnassignedIds.delete(t.workOrderTaskId);
        }
      }
      source.tasks = remainingTasks;
    }
  }

  /**
   * Day-wide capture status snapshot. Computed (not stored) from the
   * day's activities + their per-task / per-asset capture fields.
   * Matches the backend's CaptureSummary shape so the real-API swap
   * is a 1:1 read replacement.
   */
  getCaptureSummary(date: string): CaptureSummary {
    const day = this.getOrInit(date);
    // Capture is a per-CREW end-of-day action: only crew-bound activities
    // are capturable. Standalone activities (the planning rail) are excluded
    // because they're not dispatched to anyone. Mirrors the backend filter in
    // ExecutionCaptureService.GetCaptureSummary (CrewId != null).
    const allActivities: DispatchCrewJobActivity[] = day.crews.flatMap(
      (c) => c.jobs,
    );

    let totalCapturedPersonHours = 0;
    let totalCapturedKm = 0;
    let capturedCount = 0;

    const items: CaptureSummaryItem[] = allActivities.map((a) => {
      let tasksCompleted = 0;
      let tasksPartial = 0;
      let tasksNotDone = 0;
      let tasksPending = 0;
      let taskPersonHours = 0;
      const activeWorkerCount = a.workers.length;

      for (const t of a.tasks) {
        switch (t.executionStatus ?? 'Pending') {
          case 'Completed':
            tasksCompleted++;
            break;
          case 'Partial':
            tasksPartial++;
            break;
          case 'NotDone':
            tasksNotDone++;
            break;
          default:
            tasksPending++;
        }
        if (t.perWorkerHours && activeWorkerCount > 0) {
          taskPersonHours += t.perWorkerHours * activeWorkerCount;
        }
      }

      const activityLabourHours =
        ((a.travelHours ?? 0) + (a.loadingHours ?? 0) + (a.standbyHours ?? 0)) *
        activeWorkerCount;
      const totalPersonHours = taskPersonHours + activityLabourHours;

      const totalKm = (a.vehicles ?? []).reduce((sum, v) => {
        const km = v.km ?? 0;
        return sum + (v.isRoundTrip ? km * 2 : km);
      }, 0);

      // Find host crew for the row label
      const hostCrew = day.crews.find((c) =>
        c.jobs.some((j) => j.activityId === a.activityId),
      );

      if (a.isCaptured) {
        capturedCount++;
        totalCapturedPersonHours += totalPersonHours;
        totalCapturedKm += totalKm;
      }

      return {
        activityId: a.activityId,
        activityNumber: a.jobNumber,
        title: a.title,
        crewId: hostCrew?.id ?? null,
        crewName: hostCrew?.name,
        isCaptured: a.isCaptured ?? false,
        capturedAt: a.capturedAt,
        taskCount: a.tasks.length,
        tasksCompleted,
        tasksPartial,
        tasksNotDone,
        tasksPending,
        activeWorkerCount,
        totalPersonHours,
        totalKm,
        isLockedByInvoice: a.isLockedByInvoice ?? false,
      };
    });

    return {
      date,
      totalActivities: allActivities.length,
      capturedActivities: capturedCount,
      pendingActivities: allActivities.length - capturedCount,
      totalCapturedPersonHours,
      totalCapturedKm,
      activities: items,
    };
  }
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Replace the `YYYY-MM-DD` prefix of an ISO-ish timestamp with `newDate`,
 * preserving the time portion. Used when an activity is rescheduled to
 * another day — we keep the existing start/end times of day.
 *   "2026-06-03T08:00"  +  "2026-06-05"  →  "2026-06-05T08:00"
 */
function swapDatePrefix(iso: string, newDate: string): string {
  if (!iso) return `${newDate}T08:00`;
  const tIdx = iso.indexOf('T');
  if (tIdx < 0) return newDate;
  return `${newDate}${iso.slice(tIdx)}`;
}

/**
 * REPLACE an activity's workers list with the destination crew's members.
 *
 * Previously this function was a merge (kept existing workers, added new
 * ones) — but dispatchers flagged this as a serious bug: dragging an
 * activity from Crew A to Crew B left Crew A's members on the activity,
 * so the card showed workers who weren't in Crew B and the "N not in
 * Crew B" warning chip lit up immediately for every such drag.
 *
 * The correct rule is REPLACE: activities show only their host crew's
 * members by default. If the dispatcher wants to add an outside helper,
 * they explicitly drag that staff onto the activity afterwards (which
 * uses addJobWorker, separate path).
 */
function replaceWorkersWithCrewMembers(
  activity: DispatchCrewJobActivity,
  crew: DispatchCrew,
): void {
  activity.workers = crew.members.map((m) => ({
    userId: m.userId,
    name: m.name,
  }));
}

/**
 * Twin of `replaceWorkersWithCrewMembers` — REPLACE the activity's
 * vehicle list with the destination crew's vehicles. Strips
 * `allocations` from each entry (that field is a derived/read-time
 * concept for the Assets rail, never stored on activity-level entries).
 *
 * Called when an activity arrives on a crew via assign or move so the
 * dispatcher's mental model holds: "the activity inherits THIS crew's
 * gear by default; extra vehicles are added explicitly via drag."
 */
function replaceVehiclesWithCrewVehicles(
  activity: DispatchCrewJobActivity,
  crew: DispatchCrew,
): void {
  activity.vehicles = crew.vehicles.map((v) => ({
    assetId: v.assetId,
    code: v.code,
    label: v.label,
    assetClass: v.assetClass,
    imageUrl: v.imageUrl,
  }));
}

function mapUnassignedToTask(u: UnassignedJob): DispatchActivityTask {
  return {
    taskId: `t-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    workOrderTaskId: u.id,
    jobNumber: u.jobNumber,
    jobName: u.jobName,
    description: u.title,
    type: u.type,
    taskTypeRaw: u.taskType,
    scaffoldCode: u.scaffoldCode,
    clientName: u.clientName,
    siteName: u.siteName,
    // Preserve the commercial intent a dispatcher set when creating this
    // task on the board (Sprint A). Undefined for quote-origin tasks —
    // their charge resolves via the Job→WO→WOTask chain server-side. For
    // board-created tasks these are the WOTask-level override the admin
    // chose, so they must survive the link onto an activity.
    chargeUpType: u.chargeUpType,
    isBillable: u.isBillable,
  };
}

export const mockStore = new MockStore();
